/**
 * The name and image a tenant gave each deployment, kept in this browser.
 *
 * Neither is on chain: `MsgCreateDeployment` carries the manifest hash and
 * nothing a tenant would recognise. The console knows both only at the moment
 * it creates the deployment, so it records them then, keyed by wallet and
 * dseq, and the instances list reads them back.
 *
 * These are the tenant's own notes, not chain facts. A deployment created on
 * another device or with the CLI has no entry here, and the list says so
 * rather than guessing. Nothing here is ever sent anywhere.
 *
 * Storage can be unavailable or hold anything (another tab, an older build, a
 * hand edit), so every read validates and every access is guarded.
 */

import { templateSummary } from './templateCatalog'

/**
 * The SDL template a deployment was created from, and its non-secret
 * answers: what the App tab is built from. Secrets never get here, because
 * only a template's declared `answerKeys` are kept, on write and on read.
 */
export interface DeploymentTemplateOrigin {
  id: string
  values: Record<string, string>
  /** The tenant edited the SDL after the template rendered it. */
  modified: boolean
}

export interface DeploymentLabel {
  name: string
  image: string | null
  /** When it was recorded, in ms since the epoch; eviction drops the oldest. */
  recordedAt: number
  /** Absent for anything not created from a template, and for older labels. */
  template?: DeploymentTemplateOrigin
}

/** Kept small on purpose: a tenant with more deployments than this loses the oldest names. */
export const MAX_LABELS = 500
const MAX_NAME = 120
const MAX_IMAGE = 512
/** Longer than any answer a template asks for; a longer one is not ours. */
const MAX_ANSWER = 200

function key(wallet: string): string {
  return `ddws.console.labels.${wallet}`
}

/**
 * A template origin this build can honour, or null: a template that still
 * exists, and only answers to its declared non-secret questions. A bad origin
 * is dropped on its own; the label it came with still reads.
 */
function validTemplateOrigin(value: unknown): DeploymentTemplateOrigin | null {
  if (!value || typeof value !== 'object') return null
  const { id, values, modified } = value as Record<string, unknown>
  const summary = typeof id === 'string' ? templateSummary(id) : undefined
  if (!summary || typeof modified !== 'boolean' || !values || typeof values !== 'object' || Array.isArray(values)) return null
  const kept: Record<string, string> = {}
  for (const key of summary.answerKeys) {
    const answer = (values as Record<string, unknown>)[key]
    if (typeof answer === 'string' && answer.length <= MAX_ANSWER) kept[key] = answer
  }
  return { id: summary.id, values: kept, modified }
}

function validLabel(value: unknown): DeploymentLabel | null {
  if (!value || typeof value !== 'object') return null
  const { name, image, recordedAt, template } = value as Record<string, unknown>
  if (typeof name !== 'string' || name.trim() === '' || name.length > MAX_NAME) return null
  if (image !== null && (typeof image !== 'string' || image.length > MAX_IMAGE)) return null
  if (typeof recordedAt !== 'number' || !Number.isFinite(recordedAt)) return null
  const origin = template === undefined ? null : validTemplateOrigin(template)
  return origin ? { name, image, recordedAt, template: origin } : { name, image, recordedAt }
}

/** Every valid label recorded for `wallet`, by dseq. Anything unreadable reads as none. */
export function readDeploymentLabels(storage: Storage, wallet: string): Record<string, DeploymentLabel> {
  let parsed: unknown
  try {
    parsed = JSON.parse(storage.getItem(key(wallet)) ?? '{}')
  } catch {
    return {}
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
  const labels: Record<string, DeploymentLabel> = {}
  for (const [dseq, value] of Object.entries(parsed)) {
    const label = /^\d+$/.test(dseq) ? validLabel(value) : null
    if (label) labels[dseq] = label
  }
  return labels
}

function write(storage: Storage, wallet: string, labels: Record<string, DeploymentLabel>): void {
  try {
    storage.setItem(key(wallet), JSON.stringify(labels))
  } catch {
    /* storage unavailable or full: the list falls back to "Deployment #<dseq>" */
  }
}

/**
 * Record what `wallet` called deployment `dseq`. Call only after the chain has
 * accepted the deployment: a label for a dseq that never existed would name
 * nothing, and one for a failed broadcast could later name someone else's
 * retry at the same height.
 */
export function recordDeploymentLabel(
  storage: Storage,
  wallet: string,
  dseq: string,
  label: Omit<DeploymentLabel, 'recordedAt'>,
  now = Date.now(),
): void {
  const name = label.name.trim().slice(0, MAX_NAME)
  const image = label.image?.trim() ? label.image.trim().slice(0, MAX_IMAGE) : null
  const entry = validLabel({ name, image, recordedAt: now, ...(label.template ? { template: label.template } : {}) })
  if (!wallet || !/^\d+$/.test(dseq) || !entry) return
  const labels = { ...readDeploymentLabels(storage, wallet), [dseq]: entry }
  const kept = Object.entries(labels)
    .sort(([, a], [, b]) => b.recordedAt - a.recordedAt)
    .slice(0, MAX_LABELS)
  write(storage, wallet, Object.fromEntries(kept))
}

/** `window.localStorage`, or null where the browser refuses it: the getter itself throws `SecurityError`. */
export function browserStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/**
 * Record a label in this browser. Never throws, and a no-op without storage.
 *
 * The call sites run after a deployment has succeeded. A label is a
 * convenience, so nothing about it may turn that success into an error screen
 * — which is what reading `window.localStorage` at the call site did when the
 * browser blocked site data: the throw happened while arguments were being
 * evaluated, before any guard in here could run.
 */
export function rememberDeploymentLabel(wallet: string, dseq: string, label: Omit<DeploymentLabel, 'recordedAt'>): void {
  const storage = browserStorage()
  if (storage) recordDeploymentLabel(storage, wallet, dseq, label)
}

export function forgetDeploymentLabel(storage: Storage, wallet: string, dseq: string): void {
  const labels = readDeploymentLabels(storage, wallet)
  if (!(dseq in labels)) return
  delete labels[dseq]
  write(storage, wallet, labels)
}

/**
 * The label an SDL document carries: its first service's name and image, with
 * "+ N more" when there are several services. Null when there is no service.
 */
export function labelFromSdlServices(services: Record<string, unknown> | undefined): Omit<DeploymentLabel, 'recordedAt'> | null {
  const entries = Object.entries(services ?? {})
  const first = entries[0]
  if (!first) return null
  const [name, service] = first
  const image = service && typeof service === 'object' && typeof (service as { image?: unknown }).image === 'string'
    ? ((service as { image: string }).image.trim() || null)
    : null
  const more = entries.length - 1
  return { name, image: image && more > 0 ? `${image} + ${more} more` : image }
}
